/**
 * jsonDiff.js — pure structural diff for already-parsed JSON/JS values.
 *
 * Used by the paste-compare UI to diff OpenRTB bid request bodies (and any
 * other JSON payloads) side by side. Callers parse strings themselves; this
 * only ever walks already-parsed values.
 *
 * Never throws — guards cycles with a seen-set (WeakMap of left objects ->
 * right objects visited together); a revisited pair is reported as a
 * 'changed' leaf with a '[circular]' note rather than recursing forever.
 */

function typeTag(v) {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  return typeof v; // 'object' | 'string' | 'number' | 'boolean' | 'undefined' | ...
}

function joinPath(base, key, isArray) {
  if (base === '') return isArray ? `[${key}]` : String(key);
  return isArray ? `${base}[${key}]` : `${base}.${key}`;
}

export function jsonDiff(left, right, opts = {}) {
  const includeSame = !!opts.includeSame;
  const maxEntries = Number.isFinite(opts.maxEntries) ? opts.maxEntries : 2000;

  const counts = { added: 0, removed: 0, changed: 0, same: 0 };
  const entries = [];
  let truncated = false;

  // seen-set: pairs of (leftObj,rightObj) currently on the recursion stack,
  // keyed by left object identity -> Set of right objects paired with it.
  const seenLeft = new WeakMap();

  const record = (path, kind, l, r) => {
    counts[kind] += 1;
    if (kind === 'same' && !includeSame) return;
    if (entries.length >= maxEntries) { truncated = true; return; }
    entries.push({ path, kind, left: l, right: r });
  };

  const walk = (path, l, r) => {
    let cycle = false;
    let addedToSeen = null;
    if (l !== null && typeof l === 'object' && r !== null && typeof r === 'object') {
      const set = seenLeft.get(l);
      if (set && set.has(r)) {
        cycle = true;
      } else {
        if (!set) { seenLeft.set(l, new Set([r])); addedToSeen = { obj: l, r }; }
        else { set.add(r); addedToSeen = { obj: l, r }; }
      }
    }

    if (cycle) {
      record(path, 'changed', '[circular]', '[circular]');
      return;
    }

    const lUndef = l === undefined;
    const rUndef = r === undefined;
    if (lUndef && !rUndef) { record(path, 'added', l, r); cleanup(); return; }
    if (!lUndef && rUndef) { record(path, 'removed', l, r); cleanup(); return; }
    if (lUndef && rUndef) { record(path, 'same', l, r); cleanup(); return; }

    const lt = typeTag(l);
    const rt = typeTag(r);

    if (lt !== rt) {
      record(path, 'changed', l, r);
      cleanup();
      return;
    }

    if (lt === 'array') {
      const len = Math.max(l.length, r.length);
      for (let i = 0; i < len; i++) {
        const has_l = i < l.length;
        const has_r = i < r.length;
        const p = joinPath(path, i, true);
        if (has_l && !has_r) record(p, 'removed', l[i], undefined);
        else if (!has_l && has_r) record(p, 'added', undefined, r[i]);
        else walk(p, l[i], r[i]);
      }
      cleanup();
      return;
    }

    if (lt === 'object') {
      const lKeys = Object.keys(l);
      const rKeys = Object.keys(r);
      // deterministic order: left-object key order first, then any
      // right-only keys in their own order.
      const orderedKeys = [...lKeys, ...rKeys.filter((k) => !lKeys.includes(k))];
      for (const k of orderedKeys) {
        const has_l = Object.prototype.hasOwnProperty.call(l, k);
        const has_r = Object.prototype.hasOwnProperty.call(r, k);
        const p = joinPath(path, k, false);
        if (has_l && !has_r) record(p, 'removed', l[k], undefined);
        else if (!has_l && has_r) record(p, 'added', undefined, r[k]);
        else walk(p, l[k], r[k]);
      }
      cleanup();
      return;
    }

    // primitive
    if (l === r || (Number.isNaN(l) && Number.isNaN(r))) {
      record(path, 'same', l, r);
    } else {
      record(path, 'changed', l, r);
    }
    cleanup();

    function cleanup() {
      if (addedToSeen) {
        const set = seenLeft.get(addedToSeen.obj);
        if (set) set.delete(addedToSeen.r);
      }
    }
  };

  try {
    walk('', left, right);
  } catch {
    // Never throw — best-effort result up to whatever we collected.
  }

  return {
    same: counts.added === 0 && counts.removed === 0 && counts.changed === 0,
    counts,
    entries,
    ...(truncated ? { truncated: true } : {}),
  };
}

export default jsonDiff;
